import { ChevronDown } from "lucide-react-native";
import { FC, ReactNode, useState } from "react";
import { ScrollView } from "react-native";

import { Box, HStack, Pressable, Text, VStack } from "./base";

interface MobileSummaryCardProps {
  /** Uppercase label shown in the always-visible header. */
  title: string;
  /** Glanceable content shown next to the title while collapsed. */
  preview?: ReactNode;
  /** Full card body, revealed when the card is expanded. */
  children: ReactNode;
  defaultExpanded?: boolean;
}

/**
 * Mobile-only summary surface: a dark card that stays pinned at the top of a
 * step while the editor scrolls beneath it. Collapsed it shows just a title +
 * a glanceable preview; tapping expands it into the full card (slider,
 * checklist, primary CTA, …).
 */
const MobileSummaryCard: FC<MobileSummaryCardProps> = ({
  title,
  preview,
  children,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box className="bg-typography-900 rounded-2xl overflow-hidden shrink-0">
      <Pressable onPress={() => setExpanded((prev) => !prev)}>
        <HStack className="items-center gap-3 px-5 py-4">
          <VStack className="flex-1 gap-1">
            <Text className="text-[13px] uppercase tracking-widest text-white/50 font-medium">
              {title}
            </Text>
            {!expanded && preview ? <Box>{preview}</Box> : null}
          </VStack>
          <Box
            style={{ transform: [{ rotate: expanded ? "180deg" : "0deg" }] }}
          >
            <ChevronDown color="rgba(255,255,255,0.6)" size={20} />
          </Box>
        </HStack>
      </Pressable>

      {expanded ? (
        <ScrollView
          className="max-h-[420px]"
          contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 20 }}
          showsVerticalScrollIndicator={false}
        >
          {children}
        </ScrollView>
      ) : null}
    </Box>
  );
};

export default MobileSummaryCard;
